import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, Check } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { API } from "@/lib/api";
import {
  getClientFingerprint,
  isAlreadyParticipatedError,
  alreadyParticipatedMessage,
  extractErrorMessage,
} from "@/lib/fingerprint";
import ShareBoost from "@/components/ShareBoost";

/**
 * Lottery draw — public player UI.
 *
 * Flow:
 *  1. Bouton "Cliquez ici pour participer" → POST /public/games/<slug>/lottery-enter
 *     with participant + ctas_completed + optional `ref` query-string.
 *  2. On success → confirmation screen with message + share buttons.
 *     Share URL: <current /m/<merchant_slug> or /play/<slug>>?ref=<session_id>
 *     Native share via Web Share API (mobile) + fallback buttons (desktop).
 *
 * Sharing is attributed server-side when a new participant arrives via the
 * ref link; that participant's session stores `referred_by`, and the
 * referrer's `referrals_count` is incremented → more tickets at draw time.
 */
export default function LotteryDraw({
  game,
  participant,
  ctasCompleted,
  refId, // parent participant id (if arrived via share)
  receipt,
  onAlreadyParticipated,
  onSubmitted,
}) {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [alreadyEntered, setAlreadyEntered] = useState(false);

  async function submit() {
    if (submitting || done) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/public/games/${game.slug}/lottery-enter`,
        {
          participant,
          ctas_completed: ctasCompleted || [],
          ref: refId || null,
          fingerprint: getClientFingerprint(),
          receipt_url: receipt?.url || null,
          receipt_ocr: receipt?.ocr || null,
        }
      );
      setSessionId(data.session_id);
      setAlreadyEntered(!!data.already_entered);
      setDone(true);
      onSubmitted?.();
      if (data.already_entered) {
        toast.info(t("play.lottery_already_entered"));
      } else {
        toast.success(t("play.lottery_entered"));
      }
    } catch (e) {
      if (isAlreadyParticipatedError(e)) {
        onAlreadyParticipated?.(alreadyParticipatedMessage(e));
      } else {
        toast.error(extractErrorMessage(e, "Error"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  /* ----------- Render ----------- */
  if (!done) {
    return (
      <div
        className="relative mx-auto w-full max-w-[460px] text-center fade-up"
        data-testid="lottery-entry"
      >
        <div
          aria-hidden="true"
          className="absolute -inset-10 rounded-[40px] blur-3xl opacity-40 pointer-events-none"
          style={{
            background:
              "radial-gradient(closest-side, #D4AF3755 0%, transparent 70%)",
          }}
        />
        <div
          className="relative rounded-[26px] border border-white/10 shadow-[0_30px_80px_-25px_rgba(15,23,42,0.6)] overflow-hidden"
          style={{
            background:
              "radial-gradient(140% 100% at 50% 0%, #2b2418 0%, #141016 55%, #0b0a10 100%)",
          }}
        >
          <div
            aria-hidden="true"
            className="absolute inset-3 rounded-[18px] pointer-events-none"
            style={{
              border: "1px solid rgba(212,175,55,0.25)",
              boxShadow: "inset 0 0 0 1px rgba(212,175,55,0.08)",
            }}
          />
          <div className="relative px-8 py-12 sm:px-10 sm:py-14">
            <Sparkles
              className="h-5 w-5 mx-auto"
              style={{ color: "#d4af37" }}
            />
            <div
              className="mt-3 text-[10px] uppercase tracking-[0.36em] font-bold"
              style={{ color: "#d4af37" }}
            >
              {t("play.lottery_badge")}
            </div>
            <h2
              className="mt-4 text-3xl sm:text-4xl font-semibold tracking-tight leading-tight"
              style={{
                color: "#f5e4bf",
                textShadow: "0 2px 24px rgba(212,175,55,0.35)",
                fontFamily: "Outfit, ui-sans-serif, system-ui, sans-serif",
              }}
            >
              {game?.title}
            </h2>
            {game?.winners_count > 0 && (
              <p className="mt-4 text-sm" style={{ color: "rgba(245,228,191,0.65)" }}>
                {t("play.lottery_winners_label", { count: game.winners_count || 1 })}
              </p>
            )}
            <div className="mt-8 w-full px-2">
              <Button
                type="button"
                onClick={submit}
                disabled={submitting}
                data-testid="lottery-participate-btn"
                className="w-full sm:w-auto sm:mx-auto rounded-full h-12 px-5 sm:px-8 text-[11px] sm:text-xs font-bold tracking-[0.14em] sm:tracking-[0.22em] uppercase whitespace-normal leading-tight flex items-center justify-center"
                style={{
                  background:
                    "linear-gradient(180deg, #f5e4bf 0%, #d4af37 55%, #a98027 100%)",
                  color: "#1a1410",
                  boxShadow:
                    "0 14px 36px -10px rgba(212,175,55,0.55), inset 0 1px 0 rgba(255,255,255,0.35), inset 0 -1px 0 rgba(0,0,0,0.25)",
                }}
              >
                <Sparkles className="h-3.5 w-3.5 mr-2 shrink-0" />
                <span className="truncate">
                  {submitting
                    ? t("play.lottery_submitting")
                    : t("play.lottery_participate_cta")}
                </span>
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ----------- Confirmation + share ----------- */
  return (
    <div
      className="relative mx-auto w-full max-w-[520px] fade-up"
      data-testid="lottery-confirmation"
    >
      <div
        aria-hidden="true"
        className="absolute -inset-10 rounded-[40px] blur-3xl opacity-40 pointer-events-none"
        style={{
          background:
            "radial-gradient(closest-side, #10b98166 0%, transparent 70%)",
        }}
      />
      <div className="relative rounded-[26px] bg-card border border-border shadow-[0_30px_80px_-25px_rgba(15,23,42,0.4)] p-8 sm:p-10 text-center">
        <div className="mx-auto h-14 w-14 rounded-full flex items-center justify-center mb-4 bg-emerald-500/15">
          <Check className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
        </div>
        <div
          className="text-[10px] uppercase tracking-[0.36em] font-bold text-emerald-600 dark:text-emerald-400"
          data-testid="lottery-confirmation-badge"
        >
          {alreadyEntered
            ? t("play.lottery_already_entered_badge")
            : t("play.lottery_confirmed_badge")}
        </div>
        <h2 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight" data-testid="lottery-confirmed-title">
          {game.confirm_title?.trim() || t("play.lottery_confirmed_title")}
        </h2>
        <p className="mt-3 text-sm text-muted-foreground leading-relaxed max-w-md mx-auto" data-testid="lottery-confirmed-body">
          {game.confirm_body?.trim() || t("play.lottery_confirmed_body")}
        </p>

        <ShareBoost game={game} sessionId={sessionId} />

        <div className="mt-6 text-[11px] text-muted-foreground">
          {t("play.powered")}
        </div>
      </div>
    </div>
  );
}
